import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useDataScope } from "@/hooks/useDataScope";
import { formatDate, formatDateTime } from "@/lib/format";
import {
  Timer, Gauge, CheckCircle2, Truck, MapPin, ExternalLink,
  Loader2, Info, PackageCheck, AlertTriangle, Camera,
} from "lucide-react";

const DELIVERY_STYLES = {
  delivered: "bg-green-100 text-green-700",
  in_transit: "bg-blue-100 text-blue-700",
  out_for_delivery: "bg-orange-100 text-orange-700",
  pre_transit: "bg-slate-100 text-slate-600",
  return_to_sender: "bg-red-100 text-red-700",
  failure: "bg-red-100 text-red-700",
  available_for_pickup: "bg-violet-100 text-violet-700",
};

function DeliveryBadge({ status, testId }) {
  const cls = DELIVERY_STYLES[status] || "bg-slate-100 text-slate-600";
  const label = (status || "—").replace(/_/g, " ");
  return (
    <span data-testid={testId} className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${cls}`}>
      {label}
    </span>
  );
}

export default function Delivery() {
  const { param, isAdmin } = useDataScope();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/tracking/analytics${param ? `?${param}` : ""}`)
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [param]);

  return (
    <div data-testid="delivery-page" className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Delivery Tracking</h1>
          <p className="text-slate-500 mt-1">Live carrier delivery performance for the last 7 days.</p>
        </div>
      </div>

      {loading ? (
        <div data-testid="delivery-loading" className="flex flex-col items-center justify-center gap-3 text-slate-400 py-24">
          <Loader2 className="w-7 h-7 animate-spin" />
          <p className="text-sm">Fetching live delivery data… first load can take a moment while we sync carriers.</p>
        </div>
      ) : !data || data.enabled === false ? (
        <EmptyState data={data} isAdmin={isAdmin} />
      ) : (
        <DeliveryContent data={data} />
      )}
    </div>
  );
}

function EmptyState({ data, isAdmin }) {
  let msg = "Delivery tracking isn't available yet.";
  if (data?.no_store) msg = "No shipments to track in this view yet.";
  else if (data && data.configured === false) msg = "Live delivery tracking isn't enabled for your account yet.";
  else if (isAdmin) msg = "No tracked deliveries in this view yet. Try a different store from the account switcher.";
  return (
    <div data-testid="delivery-empty" className="flex items-start gap-3 bg-blue-50 border border-blue-200 text-blue-800 rounded-lg px-4 py-4">
      <Info className="w-5 h-5 mt-0.5 shrink-0" />
      <p className="text-sm">{msg}</p>
    </div>
  );
}

function DeliveryContent({ data }) {
  const dist = data.transit_distribution || [];
  const maxDist = useMemo(() => Math.max(1, ...dist.map((d) => d.count)), [dist]);

  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Timer} accent="blue" testId="kpi-avg-delivery"
          value={data.avg_delivery_days != null ? `${data.avg_delivery_days} days` : "—"}
          label="Avg delivery time" sub={`${data.delivered_count} delivered (7d)`} />
        <Kpi icon={Gauge} accent="green" testId="kpi-on-time"
          value={data.on_time_pct != null ? `${data.on_time_pct}%` : "—"}
          label="On-time delivery" sub={`${data.on_time_sample} with an ETA`} />
        <Kpi icon={CheckCircle2} accent="orange" testId="kpi-delivered"
          value={data.delivered_count} label="Delivered (last 7d)" sub={`${data.tracked_count} packages tracked`} />
        <Kpi icon={Truck} accent="violet" testId="kpi-in-transit"
          value={data.in_transit_count} label="In transit" sub="Across tracked carriers" />
      </div>

      {data.unresolved_count > 0 && (
        <div data-testid="delivery-unsupported-note" className="flex items-start gap-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-3">
          <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
          <p className="text-sm">
            <strong>{data.unresolved_count}</strong> of {data.shipment_count} shipments use carriers we can't auto-track (e.g. custom carriers).
            Only major carriers (UPS, FedEx, USPS, DHL) report live delivery scans.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-5">
            <PackageCheck className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Delivered in… (transit time)</h2>
          </div>
          {data.delivered_count === 0 ? (
            <p className="text-sm text-slate-400 py-8 text-center">No deliveries recorded in the last 7 days yet.</p>
          ) : (
            <ul className="space-y-3" data-testid="transit-distribution">
              {dist.map((d) => (
                <li key={d.label} data-testid={`transit-bucket-${d.label.replace(/\s+/g, "-")}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-slate-700">{d.label}</span>
                    <span className="text-slate-500">{d.count}</span>
                  </div>
                  <div className="mt-1.5 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-600 rounded-full transition-all duration-500" style={{ width: `${(d.count / maxDist) * 100}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <CarrierPerformance carriers={data.by_carrier || []} />
      </div>

      <DeliveredTable rows={data.delivered_rows || []} />
    </>
  );
}

function CarrierPerformance({ carriers }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6">
      <div className="flex items-center gap-2 mb-5">
        <Truck className="w-5 h-5 text-orange-500" />
        <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Carrier Performance</h2>
      </div>
      {carriers.length === 0 ? (
        <p className="text-sm text-slate-400 py-8 text-center">No carrier data yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="carrier-performance-table">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-3 font-medium">Carrier</th>
                <th className="py-2 px-3 font-medium text-right">Shipped</th>
                <th className="py-2 px-3 font-medium text-right">Delivered</th>
                <th className="py-2 px-3 font-medium text-right">Avg days</th>
                <th className="py-2 pl-3 font-medium text-right">On-time</th>
              </tr>
            </thead>
            <tbody>
              {carriers.map((c) => (
                <tr key={c.carrier} data-testid={`carrier-perf-${c.carrier}`} className="border-b border-slate-100">
                  <td className="py-3 pr-3 text-sm font-semibold text-slate-900">{c.carrier}</td>
                  <td className="py-3 px-3 text-sm text-slate-600 text-right">{c.shipped}</td>
                  <td className="py-3 px-3 text-sm text-slate-600 text-right">{c.delivered}</td>
                  <td className="py-3 px-3 text-sm text-slate-600 text-right">{c.avg_days != null ? c.avg_days : "—"}</td>
                  <td className="py-3 pl-3 text-sm text-right font-medium text-slate-700">{c.on_time_pct != null ? `${c.on_time_pct}%` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DeliveredTable({ rows }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Recent Deliveries</h2>
        <span className="text-xs text-slate-500">{rows.length} delivered (7d)</span>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-slate-400 py-8 text-center">No deliveries yet in the last 7 days.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="delivered-rows-table">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-3 font-medium">Order #</th>
                <th className="py-2 px-3 font-medium">Carrier</th>
                <th className="py-2 px-3 font-medium">Ship To</th>
                <th className="py-2 px-3 font-medium">Shipped</th>
                <th className="py-2 px-3 font-medium">Delivered</th>
                <th className="py-2 px-3 font-medium text-right">Transit</th>
                <th className="py-2 px-3 font-medium text-right">On-time</th>
                <th className="py-2 pl-3 font-medium text-right">Proof</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.tracking_number} data-testid={`delivered-row-${r.order_number}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                  <td className="py-3 pr-3 text-sm font-semibold text-slate-900">
                    {r.public_url ? (
                      <a href={r.public_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-700 inline-flex items-center gap-1">
                        {r.order_number}<ExternalLink className="w-3 h-3" />
                      </a>
                    ) : r.order_number}
                  </td>
                  <td className="py-3 px-3 text-sm text-slate-700">{r.carrier}</td>
                  <td className="py-3 px-3 text-sm text-slate-600"><span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{r.ship_to}</span></td>
                  <td className="py-3 px-3 text-sm text-slate-600">{formatDate(r.ship_date)}</td>
                  <td className="py-3 px-3 text-sm text-slate-600">{formatDateTime(r.delivered_at)}</td>
                  <td className="py-3 px-3 text-sm text-slate-700 text-right">{r.transit_days != null ? `${r.transit_days}d` : "—"}</td>
                  <td className="py-3 px-3 text-right">
                    {r.on_time === true && <DeliveryBadge status="delivered" testId={`ontime-${r.order_number}`} />}
                    {r.on_time === false && <span data-testid={`late-${r.order_number}`} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">Late</span>}
                    {r.on_time == null && <span className="text-slate-400 text-xs">—</span>}
                  </td>
                  <td className="py-3 pl-3 text-right">
                    {r.pod_images && r.pod_images.length > 0 ? (
                      <a href={r.pod_images[0]} target="_blank" rel="noreferrer" data-testid={`pod-${r.order_number}`}
                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 text-xs font-medium">
                        <Camera className="w-3.5 h-3.5" />{r.pod_images.length}
                      </a>
                    ) : <span className="text-slate-300 text-xs">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, value, label, sub, accent = "blue", testId }) {
  const tones = {
    blue: "bg-blue-50 text-blue-600",
    orange: "bg-orange-50 text-orange-600",
    green: "bg-green-50 text-green-600",
    violet: "bg-violet-50 text-violet-600",
  };
  return (
    <div data-testid={testId} className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md hover:-translate-y-0.5 transition-all">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${tones[accent]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-3xl font-bold text-slate-900 mt-3" style={{ fontFamily: "Manrope" }}>{value}</p>
      <p className="text-sm font-medium text-slate-700 mt-1">{label}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}
